import { useEffect, useRef, useState } from "react";

/** Returns `true` only once `active` has stayed true for `delayMs`, and then
 *  keeps returning `true` for at least `minVisibleMs` after `active` drops —
 *  so a loader driven by this never flashes on a fast operation, and never
 *  flickers away the instant a slow one resolves.
 *
 *  The "only on delay" half avoids the spinner-flash anti-pattern (showing a
 *  loader for a 200 ms request makes the app feel slower); the "minimum
 *  visible" half avoids a jarring show-then-vanish when the response lands
 *  just past the delay. Designed for the booking spinner, but reusable for
 *  any usually-instant async action. */
export function useDelayedFlag(
  active: boolean,
  { delayMs = 500, minVisibleMs = 500 }: { delayMs?: number; minVisibleMs?: number } = {},
): boolean {
  const [visible, setVisible] = useState(false);
  const shownAtRef = useRef<number | null>(null);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (active && !visible) {
      // Arm the reveal — only fires if still active after the delay.
      timer = setTimeout(() => {
        shownAtRef.current = Date.now();
        setVisible(true);
      }, delayMs);
    } else if (!active && visible) {
      // Honor the minimum visible time before hiding.
      const elapsed = Date.now() - (shownAtRef.current ?? 0);
      timer = setTimeout(
        () => {
          shownAtRef.current = null;
          setVisible(false);
        },
        Math.max(0, minVisibleMs - elapsed),
      );
    }
    return () => clearTimeout(timer);
  }, [active, visible, delayMs, minVisibleMs]);

  return visible;
}
